import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Grid,
  Paper,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  Close,
  Group,
  TrendingUp,
  AttachMoney,
  AccountBalance,
  Search,
  Download,
  People,
  MonetizationOn
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import { adminAPI } from '../services/api';
import { formatNumber, formatDate, formatDateTime } from '../utils/formatters';
import { ruRU } from '../locales/ruRU';
import { exportToExcel } from '../utils/exportToExcel';

const ReferralDetailsDrawer = ({ open, onClose, userId, username }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Данные
  const [stats, setStats] = useState(null);
  const [referrals, setReferrals] = useState([]);

  // Фильтры и пагинация
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');

  // Загрузка статистики
  useEffect(() => {
    if (open && userId) {
      loadStats();
    }
  }, [open, userId]);

  // Загрузка списка рефералов
  useEffect(() => {
    if (open && userId && activeTab === 1) {
      loadReferrals();
    }
  }, [open, userId, activeTab, page, pageSize, levelFilter, sortBy, sortOrder]);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await adminAPI.getUserReferralStats(userId);

      if (response.data.success) {
        setStats(response.data.data);
      } else {
        setError('Ошибка загрузки статистики');
      }
    } catch (err) {
      console.error('Ошибка загрузки статистики:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const loadReferrals = async () => {
    try {
      setLoading(true);

      const response = await adminAPI.getUserReferralsDetailed(userId, {
        page: page + 1, // API использует 1-based pagination
        limit: pageSize,
        level: levelFilter || undefined,
        sortBy,
        sortOrder
      });

      if (response.data.success) {
        setReferrals(response.data.data.referrals);
        setTotalCount(response.data.data.pagination.total);
      } else {
        setError('Ошибка загрузки рефералов');
      }
    } catch (err) {
      console.error('Ошибка загрузки рефералов:', err);
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleExportExcel = async () => {
    try {
      // Загружаем все данные для экспорта
      const response = await adminAPI.getUserReferralsDetailed(userId, {
        page: 1,
        limit: 10000, // Все записи
        level: levelFilter || undefined
      });

      if (response.data.success) {
        const data = response.data.data.referrals;

        // Определяем колонки для экспорта
        const exportColumns = [
          { field: 'id', headerName: 'ID' },
          { field: 'username', headerName: 'Username' },
          { field: 'firstName', headerName: 'Имя' },
          { field: 'lastName', headerName: 'Фамилия' },
          { field: 'telegramId', headerName: 'Telegram ID' },
          { field: 'level', headerName: 'Уровень' },
          {
            field: 'registrationDate',
            headerName: 'Дата регистрации',
            valueGetter: ({ row }) => formatDate(row.registrationDate)
          },
          { field: 'totalDeposits', headerName: 'Депозиты' },
          { field: 'depositBonus', headerName: 'Бонус от депозитов' },
          { field: 'dailyBonus', headerName: 'Ежедневный бонус' },
          { field: 'totalBonus', headerName: 'Общий бонус' },
          { field: 'activeStaking', headerName: 'Активный стейкинг' },
          { field: 'ownReferralsCount', headerName: 'Своих рефералов' },
          {
            field: 'isActive',
            headerName: 'Активен',
            valueGetter: ({ row }) => row.isActive ? 'Да' : 'Нет'
          }
        ];

        const filename = `referrals_${username}_${formatDate(new Date())}.xlsx`;
        const success = exportToExcel(data, exportColumns, filename);

        if (!success) {
          alert('Ошибка при экспорте данных');
        }
      }
    } catch (err) {
      console.error('Ошибка экспорта:', err);
      alert('Ошибка при экспорте данных');
    }
  };

  const getLevelColor = (level) => {
    const colors = {
      1: '#10b981', // green
      2: '#3b82f6', // blue
      3: '#8b5cf6', // purple
      4: '#f59e0b', // amber
      5: '#ef4444'  // red
    };
    return colors[level] || '#6b7280';
  };

  const filteredReferrals = referrals.filter(ref => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      ref.username?.toLowerCase().includes(search) ||
      ref.telegramId?.toString().includes(search) ||
      ref.firstName?.toLowerCase().includes(search) ||
      ref.lastName?.toLowerCase().includes(search)
    );
  });

  const columns = [
    {
      field: 'username',
      headerName: 'Пользователь',
      width: 180,
      renderCell: (params) => (
        <Box>
          <Typography variant="body2">{params.value}</Typography>
          <Typography variant="caption" color="textSecondary">
            ID: {params.row.telegramId}
          </Typography>
        </Box>
      )
    },
    {
      field: 'level',
      headerName: 'Уровень',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={`Ур. ${params.value}`}
          size="small"
          sx={{
            bgcolor: getLevelColor(params.value),
            color: 'white',
            fontWeight: 600
          }}
        />
      )
    },
    {
      field: 'registrationDate',
      headerName: 'Дата регистрации',
      width: 150,
      valueFormatter: (params) => formatDate(params.value)
    },
    {
      field: 'totalDeposits',
      headerName: 'Депозиты',
      width: 120,
      valueFormatter: (params) => formatNumber(params.value, 0) + ' SPERM'
    },
    {
      field: 'depositBonus',
      headerName: 'Бонус от депозита',
      width: 140,
      valueFormatter: (params) => formatNumber(params.value, 2) + ' SPERM'
    },
    {
      field: 'dailyBonus',
      headerName: 'Ежедневный бонус',
      width: 150,
      valueFormatter: (params) => formatNumber(params.value, 2) + ' SPERM'
    },
    {
      field: 'totalBonus',
      headerName: 'Всего заработано',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 600 }}>
          {formatNumber(params.value, 2)} SPERM
        </Typography>
      )
    },
    {
      field: 'activeStaking',
      headerName: 'Стейкинг',
      width: 120,
      valueFormatter: (params) => formatNumber(params.value, 0) + ' SPERM'
    },
    {
      field: 'ownReferralsCount',
      headerName: 'Рефералов',
      width: 90,
      align: 'center'
    },
    {
      field: 'isActive',
      headerName: 'Статус',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Активен' : 'Неактивен'}
          size="small"
          color={params.value ? 'success' : 'default'}
        />
      )
    }
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '90vh',
          bgcolor: '#0f172a'
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{
          p: 2,
          borderBottom: '1px solid #334155',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#1e293b'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Group sx={{ fontSize: 32, color: '#6366f1' }} />
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Реферальная структура
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Пользователь: {username}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}>
            <Close />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab label="Обзор" icon={<TrendingUp />} iconPosition="start" />
            <Tab label="Список рефералов" icon={<People />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {loading && !stats ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <>
              {/* Вкладка "Обзор" */}
              {activeTab === 0 && stats && (
                <Box>
                  {/* Общая статистика */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: '#1e293b' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#fff', fontWeight: 600 }}>
                      Общая статистика
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#6366f1', fontWeight: 600 }}>
                            {formatNumber(stats.totalStats.totalReferrals)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Всего рефералов
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#10b981', fontWeight: 600 }}>
                            {formatNumber(stats.totalStats.activeReferrals)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Активных
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 600 }}>
                            {formatNumber(stats.totalStats.totalDeposits, 0)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Общие депозиты SPERM
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6} md={3}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                            {formatNumber(stats.totalStats.totalStaking, 0)}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Общий стейкинг SPERM
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Реферальный доход */}
                  <Paper sx={{ p: 3, mb: 3, bgcolor: '#1e293b', border: '1px solid #10b981' }}>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#fff', fontWeight: 600 }}>
                      <MonetizationOn sx={{ color: '#10b981' }} />
                      Реферальный доход
                    </Typography>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h3" sx={{ color: '#10b981', fontWeight: 700 }}>
                            {formatNumber(stats.totalStats.totalBonus, 2)}
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, mt: 1, color: '#fff' }}>
                            ВСЕГО ЗАРАБОТАНО SPERM
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                            {formatNumber(stats.totalStats.totalDepositBonus, 2)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            От депозитов рефералов
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            (единовременные выплаты)
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h4" sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                            {formatNumber(stats.totalStats.totalDailyBonus, 2)}
                          </Typography>
                          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            От ежедневного дохода
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            (накопленный доход)
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Статистика по уровням */}
                  <Typography variant="h6" gutterBottom sx={{ mt: 3, color: '#fff', fontWeight: 600 }}>
                    Детализация по уровням
                  </Typography>
                  <Grid container spacing={2}>
                    {[1, 2, 3, 4, 5].map((level) => {
                      const levelData = stats.levelStats[`level${level}`];
                      return (
                        <Grid item xs={12} md={6} lg={4} key={level}>
                          <Card sx={{
                            border: '2px solid',
                            borderColor: getLevelColor(level),
                            bgcolor: `${getLevelColor(level)}10`
                          }}>
                            <CardContent>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6" sx={{ color: getLevelColor(level), fontWeight: 600 }}>
                                  Уровень {level}
                                </Typography>
                                <Chip
                                  label={`${levelData.referralCount} чел.`}
                                  size="small"
                                  sx={{ bgcolor: getLevelColor(level), color: 'white' }}
                                />
                              </Box>

                              <Box sx={{ mb: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                  Активных: {levelData.activeCount}
                                </Typography>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Депозиты: {formatNumber(levelData.totalDeposits, 0)} SPERM
                                </Typography>
                              </Box>

                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Стейкинг: {formatNumber(levelData.totalStaking, 0)} SPERM
                                </Typography>
                              </Box>

                              <Divider sx={{ my: 2 }} />

                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                  Бонус от депозитов:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                                  {formatNumber(levelData.depositBonus, 2)} SPERM
                                </Typography>
                              </Box>

                              <Box sx={{ mb: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                  Ежедневный бонус:
                                </Typography>
                                <Typography variant="body1" sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                                  {formatNumber(levelData.dailyBonus, 2)} SPERM
                                </Typography>
                              </Box>

                              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.3)', borderRadius: 1 }}>
                                <Typography variant="body2" color="textSecondary">
                                  Всего заработано:
                                </Typography>
                                <Typography variant="h6" sx={{ color: getLevelColor(level), fontWeight: 700 }}>
                                  {formatNumber(levelData.totalBonus, 2)} SPERM
                                </Typography>
                              </Box>

                              {levelData.referralCount > 0 && (
                                <Box sx={{ mt: 2 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={(levelData.activeCount / levelData.referralCount) * 100}
                                    sx={{
                                      height: 8,
                                      borderRadius: 4,
                                      bgcolor: 'rgba(0,0,0,0.1)',
                                      '& .MuiLinearProgress-bar': {
                                        bgcolor: getLevelColor(level)
                                      }
                                    }}
                                  />
                                  <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                    Активность: {Math.round((levelData.activeCount / levelData.referralCount) * 100)}%
                                  </Typography>
                                </Box>
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* Вкладка "Список рефералов" */}
              {activeTab === 1 && (
                <Box>
                  {/* Фильтры и поиск */}
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          placeholder="Поиск по имени или ID..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search />
                              </InputAdornment>
                            )
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Уровень</InputLabel>
                          <Select
                            value={levelFilter}
                            onChange={(e) => {
                              setLevelFilter(e.target.value);
                              setPage(0); // Сброс на первую страницу
                            }}
                            label="Уровень"
                          >
                            <MenuItem value="">Все уровни</MenuItem>
                            <MenuItem value="1">Уровень 1</MenuItem>
                            <MenuItem value="2">Уровень 2</MenuItem>
                            <MenuItem value="3">Уровень 3</MenuItem>
                            <MenuItem value="4">Уровень 4</MenuItem>
                            <MenuItem value="5">Уровень 5</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth>
                          <InputLabel>Сортировка</InputLabel>
                          <Select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            label="Сортировка"
                          >
                            <MenuItem value="created_at">По дате регистрации</MenuItem>
                            <MenuItem value="total_deposits">По депозитам</MenuItem>
                            <MenuItem value="total_earned">По заработку</MenuItem>
                            <MenuItem value="username">По имени</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <Button
                          fullWidth
                          variant="outlined"
                          startIcon={<Download />}
                          onClick={handleExportExcel}
                        >
                          Excel
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* DataGrid с виртуализацией */}
                  <Paper sx={{ height: 600 }}>
                    {loading && <LinearProgress />}
                    <DataGrid
                      rows={filteredReferrals}
                      columns={columns}
                      pageSize={pageSize}
                      page={page}
                      onPageChange={(newPage) => setPage(newPage)}
                      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                      rowsPerPageOptions={[25, 50, 100]}
                      rowCount={totalCount}
                      paginationMode="server"
                      loading={loading}
                      disableSelectionOnClick
                      localeText={ruRU}
                      getRowId={(row) => row.id}
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)'
                        }
                      }}
                    />
                  </Paper>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default ReferralDetailsDrawer;
